// HTTP client. Injects the access token, and refreshes once on a 401 before
// giving up, so an access token expiring mid-session is invisible to the user
// rather than throwing them back to the login screen.

// Session refresh lives in a HttpOnly cookie; bearer access stays in memory.
localStorage.removeItem('ugc.access')
localStorage.removeItem('ugc.refresh')
let accessToken: string | null = null
export type Tokens = { access_token: string; expires_in: number }
export const tokens = {
  access: () => accessToken,
  refresh: () => true,
  set(t: Tokens) { accessToken = t.access_token; resetMediaTokenCache() },
  clear() { accessToken = null; resetMediaTokenCache() },
}

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

// A single in-flight refresh is shared by every request that got a 401 at the
// same moment. Without this, five parallel queries on a stale token fire five
// refreshes, and the losers exchange a token that has already been rotated.
let refreshing: Promise<boolean> | null = null

export async function refreshTokens(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST', headers: { 'X-ReelWorks-Request': '1' }, credentials: 'same-origin',
        })
        if (res.status === 409) {
          await new Promise(resolve => setTimeout(resolve, 250 * (attempt + 1)))
          continue
        }
        if (!res.ok) { tokens.clear(); return false }
        const body = await res.json()
        tokens.set(body.tokens)
        return true
      }
      return false
    })().catch(() => false).finally(() => { refreshing = null })
  }
  return refreshing
}

async function raw(path: string, init: RequestInit, retry = true): Promise<Response> {
  const headers = new Headers(init.headers)
  headers.set('X-ReelWorks-Request', '1')
  const token = tokens.access()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(path, { ...init, headers })
  if (res.status === 401 && retry && tokens.refresh()) {
    if (await refreshTokens()) return raw(path, init, false)
    // Refresh failed: the session is genuinely over.
    tokens.clear()
    window.dispatchEvent(new Event('reelworks:signed-out'))
  }
  return res
}

async function unwrap<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T
  const text = await res.text()
  let body: any = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    // A non-JSON body on an error is still worth surfacing verbatim.
    if (!res.ok) throw new ApiError(res.status, text.slice(0, 200) || res.statusText)
  }
  if (!res.ok) throw new ApiError(res.status, body?.error ?? res.statusText)
  return body as T
}

export const api = {
  get: <T,>(path: string) => raw(path, { method: 'GET' }).then(r => unwrap<T>(r)),

  post: <T,>(path: string, body?: unknown) =>
    raw(path, {
      method: 'POST',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    }).then(r => unwrap<T>(r)),

  del: <T,>(path: string) => raw(path, { method: 'DELETE' }).then(r => unwrap<T>(r)),

  put: <T,>(path: string, body: unknown) =>
    raw(path, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(r => unwrap<T>(r)),

  patch: <T,>(path: string, body: unknown) =>
    raw(path, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(r => unwrap<T>(r)),

  // Multipart: the browser must set its own Content-Type so the boundary is
  // correct, so nothing is set here.
  upload: <T,>(path: string, file: File, onProgress?: (p: UploadProgress) => void) =>
    uploadWithProgress<T>(path, file, onProgress),
}

export type UploadProgress = {
  loaded: number
  total: number
  /** 0-1, or null while the server has the bytes but has not yet replied.
   *  Probing a video's duration takes a moment, and a bar frozen at 100% reads
   *  as a hang -- callers use null to switch to an indeterminate state. */
  fraction: number | null
  bytesPerSecond: number
  secondsRemaining: number | null
}

/** Uploads via XHR rather than fetch, because fetch cannot report upload
 *  progress: its streaming request bodies are not supported widely enough to
 *  rely on, and without progress a 500 MB video looks like a frozen page for
 *  several minutes.
 *
 *  Retries once on 401 the way the fetch client does, so a token expiring
 *  mid-upload is invisible rather than losing the transfer. */
function uploadWithProgress<T>(
  path: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
  retry = true,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const form = new FormData()
    form.append('file', file)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', path)
    const token = tokens.access()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    const started = Date.now()
    xhr.upload.onprogress = e => {
      if (!onProgress || !e.lengthComputable) return
      const seconds = (Date.now() - started) / 1000
      const rate = seconds > 0 ? e.loaded / seconds : 0
      const left = rate > 0 ? (e.total - e.loaded) / rate : null
      onProgress({
        loaded: e.loaded,
        total: e.total,
        fraction: e.total > 0 ? e.loaded / e.total : null,
        bytesPerSecond: rate,
        secondsRemaining: left,
      })
    }

    // The bytes are all sent, but the server still has to store and probe the
    // file. Reported as indeterminate so the bar stops rather than sitting at
    // a suspicious 100%.
    xhr.upload.onload = () => {
      onProgress?.({
        loaded: file.size, total: file.size,
        fraction: null, bytesPerSecond: 0, secondsRemaining: null,
      })
    }

    xhr.onload = async () => {
      if (xhr.status === 401 && retry && tokens.refresh()) {
        if (await refreshTokens()) {
          resolve(await uploadWithProgress<T>(path, file, onProgress, false))
          return
        }
        tokens.clear()
        window.dispatchEvent(new Event('reelworks:signed-out'))
      }
      let body: any = null
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null
      } catch { /* fall through to the status-based message */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as T)
        return
      }
      reject(new ApiError(xhr.status, body?.error ?? xhr.statusText ?? 'Upload failed'))
    }

    xhr.onerror = () => reject(new ApiError(0, 'Network error during upload'))
    xhr.onabort = () => reject(new ApiError(0, 'Upload cancelled'))
    xhr.ontimeout = () => reject(new ApiError(0, 'Upload timed out'))

    xhr.send(form)
  })
}

export const fileURL = (workspaceID: string, key: string) =>
  `/api/workspaces/${workspaceID}/files/${key}`

// A short-lived token that authorises media URLs, cached until close to
// expiry. A <video> src cannot send a header, and fetching a whole video as a
// blob just to attach one means downloading it in full before playback starts
// -- which on a large demo looks exactly like a play button that does nothing.
let fileTokenEpoch = 0
function resetMediaTokenCache() {
  fileTokenEpoch++
  fileTokenValue = ''
  fileTokenExpires = 0
  fileTokenInFlight = null
}
let fileTokenValue = ''
let fileTokenExpires = 0
let fileTokenInFlight: Promise<string> | null = null

export async function fileToken(): Promise<string> {
  if (fileTokenValue && Date.now() < fileTokenExpires) return fileTokenValue
  if (!fileTokenInFlight) {
    const epoch = fileTokenEpoch
    fileTokenInFlight = raw('/api/auth/file-token', { method: 'GET' })
      .then(r => unwrap<{ token: string; expires_in: number }>(r))
      .then(b => {
        if (epoch !== fileTokenEpoch) return fileToken()
        fileTokenValue = b.token
        // Renewed early, so a stream never starts on a token about to expire.
        fileTokenExpires = Date.now() + (b.expires_in - 60) * 1000
        return b.token
      })
      .finally(() => { if (epoch === fileTokenEpoch) fileTokenInFlight = null })
  }
  return fileTokenInFlight
}

/** A directly playable/loadable URL for a stored file. */
export async function mediaURL(workspaceID: string, key: string): Promise<string> {
  const t = await fileToken()
  return `${fileURL(workspaceID, key)}?t=${encodeURIComponent(t)}`
}

// blobURL fetches an authorised file and returns an object URL. Callers must
// revoke it when the element unmounts or the browser leaks the blob.
export async function blobURL(workspaceID: string, key: string, thumbnail = false): Promise<string> {
  const path = `${fileURL(workspaceID, key)}${thumbnail ? '?variant=thumbnail' : ''}`
  const res = await raw(path, { method: 'GET' })
  if (!res.ok) throw new ApiError(res.status, 'could not load file')
  return URL.createObjectURL(await res.blob())
}
